import { Box, Grid, Paper, Typography } from '@mui/material';
import PageHeader from 'components/BaseLayout/PageHeader';
import {
  NotificationContextProvider,
  useNotificationContext
} from 'core/providers/NotificationContextProvider';
import { getUserData } from 'core/store/actions/userData.actions';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../core/hooks/hooks';
import { budgetsSelector } from '../../core/store/selectors/budgets.selector';
import NotificationPanel from '../Shared/NotificationPanel';
import AccumulationProgress from './AccumulationProgress';
import BackToButton from 'components/Shared/BackToButton';
import AccumulationStats from './AccumulationStats';
import EditBudgetForm from 'components/Budgets/BudgetList/EditBudgetForm';
import AccumulationActions from './AccumulationActions';
import AccumulationCloseConfirmPanel from './AccumulationCloseConfirmPanel';
import { closeBudget } from 'core/store/actions/budgets.actions';

const AccumulationPage = () => {
  const dispatch = useAppDispatch();
  const { accumulationId } = useParams<{ accumulationId: string }>();
  const [isEditPanelOpen, setIsEditPanelOpen] = useState(false);
  const [isCloseConfirmPanelOpen, setIsCloseConfirmPanelOpen] = useState(false);
  const { showNotification } = useNotificationContext();

  const { budgets, lastAction, error } = useAppSelector(budgetsSelector);
  const accumulation = useMemo(
    () => budgets.find(b => b.id === accumulationId),
    [accumulationId, budgets]
  );

  useEffect(() => {
    switch (lastAction) {
      case closeBudget.fulfilled.type:
        showNotification({
          message: `Accumulation has been closed`
        });
        break;
      case closeBudget.rejected.type:
        showNotification({ isError: true, message: `Error: ${error}` });
        break;
    }
  }, [lastAction]);

  useEffect(() => {
    dispatch(getUserData());
  }, []);

  const handleEditAccumulation = () => {
    setIsEditPanelOpen(true);
  };

  const handleCloseAccumulation = () => {
    setIsCloseConfirmPanelOpen(true);
  };

  const closeEditForm = () => {
    setIsEditPanelOpen(false);
  };

  return accumulation ? (
    <Box
      sx={{
        mt: {
          xs: '100px',
          md: 0
        },
        mb: 9
      }}
    >
      <BackToButton />
      <PageHeader
        title={accumulation.name}
        buttons={{
          buttons: [
            { title: 'Edit', onClick: handleEditAccumulation },
            ...(!accumulation.closed
              ? [{ title: 'Close', onClick: handleCloseAccumulation }]
              : [])
          ]
        }}
      />
      <Paper sx={{ width: '100%', p: 3, mb: 2 }}>
        <Grid container spacing={0}>
          <Grid
            item
            xs={12}
            md={8}
            sx={{ borderRight: '1px solid #e0e0e0', pr: 4 }}
          >
            <AccumulationProgress accumulation={accumulation} />
          </Grid>
          <Grid item xs={12} md={4} sx={{ pl: 4 }}>
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Accumulation Details
              </Typography>
              <AccumulationStats accumulation={accumulation} />
              <AccumulationActions
                accumulation={accumulation}
                onCloseAccumulation={handleCloseAccumulation}
              />
            </Box>
          </Grid>
        </Grid>
      </Paper>
      <NotificationPanel />
      <EditBudgetForm
        isOpen={isEditPanelOpen}
        closePanel={closeEditForm}
        budgetId={accumulationId}
      />
      <AccumulationCloseConfirmPanel
        isOpen={isCloseConfirmPanelOpen}
        onClose={() => setIsCloseConfirmPanelOpen(false)}
        accumulation={accumulation}
      />
    </Box>
  ) : (
    <></>
  );
};

const PageWithNotificationProvider = () => (
  <NotificationContextProvider>
    <AccumulationPage />
  </NotificationContextProvider>
);

export default PageWithNotificationProvider;
